// WalletConnect configuration and CAIP-2 chain mapping — pure data + lookups

import type { NetworkId } from '../../shared/types/index.ts';
import type { Result } from '../../shared/types/index.ts';
import { ok, err } from '../../shared/types/index.ts';

export type WcAppMetadata = Readonly<{
  readonly name: string;
  readonly description: string;
  readonly url: string;
  readonly icons: ReadonlyArray<string>;
}>;

// WalletConnect relay — .org is deprecated/unreliable, .com is the current primary
export const WC_RELAY_URL = 'wss://relay.walletconnect.com';

export const WC_APP_METADATA: WcAppMetadata = Object.freeze({
  name: 'ChainSale',
  description: 'Fast retail crypto payments',
  url: 'https://chainsale.app',
  icons: Object.freeze(['https://chainsale.app/icon.png']),
});

export const CAIP2_CHAIN_MAP: Readonly<Record<NetworkId, string>> = Object.freeze({
  ethereum: 'eip155:1',
  polygon: 'eip155:137',
  arbitrum: 'eip155:42161',
  optimism: 'eip155:10',
  base: 'eip155:8453',
  bsc: 'eip155:56',
  avalanche: 'eip155:43114',
  hyperevm: 'eip155:999', // inactive (M63) — in wagmi CHAINS; UI gated by registry isActive
  bitcoin: 'bip122:000000000019d6689c085ae165831e93',
  litecoin: 'bip122:12a765e31ffd4059bada1e25190f6e98',
  solana: 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
  tron: 'tron:0x2b6653dc',
  xrp: 'xrpl:0',
  ton: 'ton:-239',
  sui: 'sui:mainnet', // inactive for v1 — kept for future integration
});

export const REVERSE_CAIP2_MAP: Readonly<Record<string, NetworkId>> = Object.freeze(
  Object.fromEntries(
    Object.entries(CAIP2_CHAIN_MAP).map(([k, v]) => [v, k as NetworkId]),
  ) as Record<string, NetworkId>,
);

export const WC_EVM_CHAIN_IDS: ReadonlyArray<number> = Object.freeze([
  1, 137, 42161, 10, 8453, 56, 43114, 999,
]);

export const networkIdToCaip2 = (networkId: NetworkId): Result<string> => {
  const caip2 = CAIP2_CHAIN_MAP[networkId];
  return caip2 !== undefined
    ? ok(caip2)
    : err(`Unknown networkId: ${networkId}`);
};

export const caip2ToNetworkId = (caip2: string): Result<NetworkId> => {
  const networkId = REVERSE_CAIP2_MAP[caip2];
  return networkId !== undefined
    ? ok(networkId)
    : err(`Unknown CAIP-2 chain ID: ${caip2}`);
};

export const getEvmChainId = (networkId: NetworkId): Result<number> => {
  const caip2 = CAIP2_CHAIN_MAP[networkId];
  if (caip2 === undefined) return err(`Unknown networkId: ${networkId}`);
  if (!caip2.startsWith('eip155:')) return err(`Not an EVM network: ${networkId}`);
  return ok(Number(caip2.slice(7)));
};
